import {
  Badge,
  BlockStack,
  Box,
  Card,
  Divider,
  InlineGrid,
  InlineStack,
  Text,
} from '@shopify/polaris'
import type { BccData } from './useBccData'
import type { PipelineSegment } from '../../sdk/fulfillment.api'
import { CardHeader } from './LensBadge'

// ── Helpers ───────────────────────────────────────────────────────────────────

function egp(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M EGP`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K EGP`
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 0 })} EGP`
}

function egpShort(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K`
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

// ── Stage row ─────────────────────────────────────────────────────────────────

type StageProps = {
  label:      string
  sublabel?:  string
  orders:     number
  amount:     number
  tone?:      'critical' | 'success' | 'attention' | 'info'
  isLast?:    boolean
}

function Stage({ label, sublabel, orders, amount, tone, isLast }: StageProps) {
  const borderColor =
    tone === 'critical'  ? 'border-critical' :
    tone === 'success'   ? 'border-success'  :
    tone === 'attention' ? 'border-caution'  :
    'border-secondary'

  return (
    <BlockStack gap="0">
      <Box
        borderColor={borderColor}
        borderWidth="025"
        borderRadius="200"
        padding="300"
        background={tone === 'critical' ? 'bg-surface-critical' : tone === 'success' ? 'bg-surface-success' : 'bg-surface-secondary'}
      >
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="050">
            <InlineStack gap="200" blockAlign="center">
              <Text as="p" variant="bodyMd" fontWeight="semibold">{label}</Text>
              {tone === 'critical' && <Badge tone="critical">Blocked</Badge>}
              {tone === 'success'  && <Badge tone="success">Collected</Badge>}
            </InlineStack>
            {sublabel && (
              <Text as="p" variant="bodySm" tone="subdued">{sublabel}</Text>
            )}
          </BlockStack>
          <BlockStack gap="050" inlineAlign="end">
            <Text as="p" variant="bodyMd" fontWeight="semibold"
              tone={tone === 'critical' ? 'critical' : tone === 'success' ? 'success' : undefined}
            >
              {egp(amount)}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {orders.toLocaleString('en-US')} {orders === 1 ? 'order' : 'orders'}
            </Text>
          </BlockStack>
        </InlineStack>
      </Box>

      {!isLast && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2px 0' }}>
          <div style={{
            width: 1,
            height: 16,
            background: 'var(--p-color-border)',
          }} />
        </div>
      )}
    </BlockStack>
  )
}

// ── Profit summary tile ───────────────────────────────────────────────────────

type SummaryTileProps = {
  label:  string
  value:  string
  tone?:  'success' | 'critical' | 'caution'
}

function SummaryTile({ label, value, tone }: SummaryTileProps) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
        <Text as="p" variant="headingMd"
          tone={tone === 'success' ? 'success' : tone === 'critical' ? 'critical' : undefined}
        >
          {value}
        </Text>
      </BlockStack>
    </Card>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

type Props = { data: BccData }

export function MoneyPipeline({ data }: Props) {
  const { fulfillment, cash, dashboard } = data
  const { pipeline } = fulfillment

  const confirmed: PipelineSegment = {
    orderCount:       pipeline.readyToShip.orderCount + pipeline.blockedHasPO.orderCount + pipeline.blockedNoSolution.orderCount,
    totalCodAmount:   pipeline.readyToShip.totalCodAmount + pipeline.blockedHasPO.totalCodAmount + pipeline.blockedNoSolution.totalCodAmount,
    totalNetRevenue:  pipeline.readyToShip.totalNetRevenue + pipeline.blockedHasPO.totalNetRevenue + pipeline.blockedNoSolution.totalNetRevenue,
    thisMonthRevenue: pipeline.readyToShip.thisMonthRevenue + pipeline.blockedHasPO.thisMonthRevenue + pipeline.blockedNoSolution.thisMonthRevenue,
    nextMonthRevenue: pipeline.readyToShip.nextMonthRevenue + pipeline.blockedHasPO.nextMonthRevenue + pipeline.blockedNoSolution.nextMonthRevenue,
  }

  const blocked: PipelineSegment = {
    orderCount:       pipeline.blockedHasPO.orderCount + pipeline.blockedNoSolution.orderCount,
    totalCodAmount:   pipeline.blockedHasPO.totalCodAmount + pipeline.blockedNoSolution.totalCodAmount,
    totalNetRevenue:  pipeline.blockedHasPO.totalNetRevenue + pipeline.blockedNoSolution.totalNetRevenue,
    thisMonthRevenue: pipeline.blockedHasPO.thisMonthRevenue + pipeline.blockedNoSolution.thisMonthRevenue,
    nextMonthRevenue: pipeline.blockedHasPO.nextMonthRevenue + pipeline.blockedNoSolution.nextMonthRevenue,
  }

  // Derive profit portion using overall net margin ratio
  const netMarginRatio = dashboard.revenue.net > 0
    ? dashboard.profit.net / dashboard.revenue.net
    : 0

  const profitCollected  = cash.collectedThisMonth * netMarginRatio
  const profitPending    = cash.pendingThisMonth * netMarginRatio
  const profitInTransit  = pipeline.inTransit.totalCodAmount * netMarginRatio
  const profitAtRisk     = blocked.totalCodAmount * netMarginRatio

  const blockedTopSku = fulfillment.alerts.find(a => a.type === 'blocked_no_po' || a.type === 'po_too_late')

  // Orders behind cash.collectedThisMonth — same cash lens, same population.
  const collectedOrderCount = cash.bridge.cohorts.reduce((s, c) => s + c.orderCount, 0)

  return (
    <Card>
      <BlockStack gap="400">
        {/* Cash lens: every row below is money in flight or arrived, keyed to
            where it sits today — not to the period the order was placed. */}
        <CardHeader title="Money Pipeline" lens="cash" />

        {/* Confirmed is the sum of the two rows beneath it, not a stage before
            them. Rendering it inside the chain made the same money look like it
            was counted twice (§9.9-5), so it sits above the chain as a total. */}
        <Box background="bg-surface-secondary" padding="300" borderRadius="200">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="025">
              <Text as="p" variant="bodyMd" fontWeight="semibold">Confirmed, awaiting fulfilment</Text>
              <Text as="p" variant="bodySm" tone="subdued">Ready to ship plus blocked, combined</Text>
            </BlockStack>
            <BlockStack gap="025" inlineAlign="end">
              <Text as="p" variant="headingMd">{egp(confirmed.totalCodAmount)}</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {`${confirmed.orderCount.toLocaleString('en-US')} ${confirmed.orderCount === 1 ? 'order' : 'orders'}`}
              </Text>
            </BlockStack>
          </InlineStack>
        </Box>

        <BlockStack gap="0">
          <Stage
            label="Ready to Ship"
            sublabel="Stock available — waiting pickup"
            orders={pipeline.readyToShip.orderCount}
            amount={pipeline.readyToShip.totalCodAmount}
          />
          {blocked.orderCount > 0 && (
            <Stage
              label="Blocked"
              sublabel={blockedTopSku ? blockedTopSku.message : 'Missing stock'}
              orders={blocked.orderCount}
              amount={blocked.totalCodAmount}
              tone="critical"
            />
          )}
          <Stage
            label="In Transit"
            sublabel="With carrier"
            orders={pipeline.inTransit.orderCount}
            amount={pipeline.inTransit.totalCodAmount}
            tone="info"
          />
          <Stage
            label="Waiting Settlement"
            // computeCashPosition deliberately applies no month filter here —
            // outstanding COD can be months old and all of it is at risk.
            sublabel="Delivered — pending remittance (all time)"
            orders={cash.unsettledOrders}
            amount={cash.totalCodAtRisk}
            tone="attention"
          />
          <Stage
            label="Collected"
            sublabel="Settled this month"
            // Order count comes from the settlement bridge, not from
            // dashboard.settlements.settledCount: that counts orders *created*
            // this month, while the amount below is everything *settled* this
            // month whenever it was placed. Pairing them divided one lens by
            // the other. See docs §9.3-7.
            orders={collectedOrderCount}
            amount={cash.collectedThisMonth}
            tone="success"
            isLast
          />
        </BlockStack>

        <Divider />

        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">Profit breakdown</Text>
          <InlineGrid columns={4} gap="200">
            <SummaryTile
              label="Profit collected"
              value={`EGP ${egpShort(profitCollected)}`}
              tone="success"
            />
            <SummaryTile
              label="Awaiting settlement"
              value={`EGP ${egpShort(profitPending)}`}
              tone="caution"
            />
            <SummaryTile
              label="In transit (est.)"
              value={`EGP ${egpShort(profitInTransit)}`}
            />
            <SummaryTile
              label="At risk (blocked)"
              value={`EGP ${egpShort(profitAtRisk)}`}
              tone={profitAtRisk > 0 ? 'critical' : undefined}
            />
          </InlineGrid>
        </BlockStack>
      </BlockStack>
    </Card>
  )
}
